import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BACKGROUND_COLOR, WORK_HISTORY } from "../constants";
import { getMonthDifference } from "../utils/getMonthDifference";
import styled from "styled-components";
import { isOverflowing } from "../utils/isOverflowing";
import { Position as PositionType } from "../../types/WorkHistory";
import "./Position.css";
import { HEIGHT_BUFFER } from "../constants/styles";

const PositionWrapper = styled.div<{ height: number }>`
  height: ${({ height }) => height}px;

  &::before {
    background-color: ${BACKGROUND_COLOR};
  }

  .position-description {
    height: ${({ height }) => height - 10}px;
  }

  &.is--expanded .position-description  {
    .position-description-detail {
      background-color: ${BACKGROUND_COLOR};
    }

    button {
      background-color: ${BACKGROUND_COLOR};
    }
  }
`;

export interface PositionProps {
  companyName: string;
  position: (typeof WORK_HISTORY)[0]["positions"][0];
  expandedPosition: (typeof WORK_HISTORY)[0]["positions"][0] | null;
  setExpandedPosition: React.Dispatch<React.SetStateAction<PositionType | null>>
}

export default function Position({ companyName, position, expandedPosition, setExpandedPosition }: PositionProps) {
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const positionDescriptionRef = useRef<HTMLDivElement>(null);

  const height = useMemo(() => {
    return getMonthDifference(position.from, position.to) * HEIGHT_BUFFER;
  }, [position.from, position.to]);

  const handleReadMore = useCallback(() => {
    setExpandedPosition(position);
  }, [position, setExpandedPosition]);

  const handleHide = useCallback(() => {
    setExpandedPosition(null);
  }, [setExpandedPosition]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (containerRef.current && positionDescriptionRef.current)
      timeout = setTimeout(() => containerRef.current && positionDescriptionRef.current && setIsTruncated(isOverflowing(height, positionDescriptionRef.current)), 100);

    const resize = () => {
      if (containerRef.current && positionDescriptionRef.current) {
        setIsTruncated(isOverflowing(height, positionDescriptionRef.current));
        setExpandedPosition(null);
      }
    };

    window.addEventListener("resize", resize);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", resize);
    };
  }, [position.title, height, setExpandedPosition]);

  const isExpanded = expandedPosition === position;

  const positionClassName = useMemo(() => {
    let className = "position ";
    className += position.float === "right" ? "is--position-right" : "is--position-left";
    if (isExpanded) className += " is--expanded";
    if (isTruncated && !isExpanded) className += " is--truncated";

    return className
  }, [position.float, isExpanded, isTruncated]);


  return (
    <PositionWrapper
      className={positionClassName}
      height={height}
      ref={containerRef}
    >
      <div className="position-description" ref={positionDescriptionRef}>
        <div className="position-description-detail">
          <div className="position-title">
            <strong>{companyName} | {position.title}</strong> <i>{position.from.toLocaleDateString("en-US")} - {position.to.toLocaleDateString("en-US")}</i>
          </div>
          <p>
            {position.description.split("\n").map((line, index) => (<span key={index}>{line}<br /></span>))}
          </p>
          {isTruncated && isExpanded ? <button onClick={handleHide}>Hide</button> : null}
        </div>
      </div>
      {isTruncated && !isExpanded && <div className="position-read-more"><button onClick={handleReadMore}>Read more...</button></div>}
      <div className="position-tenure"></div>
    </PositionWrapper>
  );
}
